import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { usePopoverDismiss } from '../hooks/usePopoverDismiss';
import './UserMenu.css';

export function UserMenu() {
  const { t } = useTranslation('auth');
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  usePopoverDismiss(open, wrapperRef, () => setOpen(false));

  const openLoginModal = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('login', '1');
    navigate(location.pathname + `?${params.toString()}` + location.hash);
  }, [location.pathname, location.search, location.hash, navigate]);

  const handleLogout = useCallback(async () => {
    setOpen(false);
    await logout();
  }, [logout]);

  if (loading) {
    return <div className="user-menu user-menu-loading" aria-hidden="true" />;
  }

  if (!user) {
    return (
      <button
        type="button"
        className="user-menu-login"
        onClick={openLoginModal}
        aria-label={t('login')}
      >
        {t('login')}
      </button>
    );
  }

  return (
    <div className="user-menu" ref={wrapperRef}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user.nickname}
      >
        {user.avatarUrl ? (
          <img
            className="user-menu-avatar"
            src={user.avatarUrl}
            alt=""
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="user-menu-avatar user-menu-avatar-fallback" aria-hidden="true">
            {user.nickname.slice(0, 1)}
          </span>
        )}
        <span className="user-menu-nickname">{user.nickname}</span>
      </button>
      {open && (
        <div className="user-menu-dropdown" role="menu">
          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate(`${lang ? `/${lang}` : ''}/profile`);
            }}
          >
            프로필
          </button>
          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate(`${lang ? `/${lang}` : ''}/my-races`);
            }}
          >
            내 대회
          </button>
          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={handleLogout}
          >
            {t('logout')}
          </button>
        </div>
      )}
    </div>
  );
}
